<template>
  <div class="cart">
    <div v-if="order && order !== 'fail'" class="orderList">
      <div class="cartItem">
        <div class="requestAt mb5 notranslate">
          <i class="orderId">{{locale.reservation_order_id}} {{order.order_id}}</i>
          <p class="paymentStatus">
            {{status}}
          </p>
        </div>
        <div class="inner">
          <div class="row">
            <span class="label toCapitalize notranslate">{{locale.reservation_use_space}}</span>
            <div class="value">{{summary.space.name[lang]}}</div>
          </div>
          <div class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_use_date}}</span>
            <div class="value">{{summary.date}}</div>
          </div>
          <div class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_use_time}}</span>
            <div class="value">{{summary.time.name}}</div>
          </div>
          <div class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_use_person}}</span>
            <div class="value">{{summary.person}}</div>
          </div>
          <div class="row">
            <span class="label toCapitalize notranslate">{{locale.reservation_use_section}}/{{locale.reservation_use_place}}</span>
            <div v-if="!autoAssign" class="value">
              <div v-for="(info, index) in seats"
                :key="index">
                {{info.section.name[lang]}}
                <span v-if="showPlaceName || showSeatName">
                  -
                  <span v-if="showPlaceName">{{info.place.name[lang]}}</span>
                  <span v-if="showPlaceName && showSeatName"> / </span>
                  <span v-if="showSeatName">{{info.seat.name[lang]}}</span>
                </span>
              </div>
            </div>
            <div v-else-if="seats && seats.section" class="value">
              {{ seats.section.name[lang] }} / {{ seats.place.name[lang] }}
            </div>
            <div v-else class="value">-</div>
          </div>
          <div v-if="summary.firstName" class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_book_first_name}}</span>
            <div class="value">{{summary.firstName}}</div>
          </div>
          <div v-if="summary.lastName" class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_book_last_name}}</span>
            <div class="value">{{summary.lastName}}</div>
          </div>
          <div v-if="summary.phone" class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_book_phone}}</span>
            <div class="value">{{summary.phone}}</div>
          </div>
          <div v-if="summary.email" class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_book_email}}</span>
            <div class="value">{{summary.email}}</div>
          </div>
          <div class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_reservation_date}}</span>
            <div class="value">{{setDateTimeFormat(order.createat)}}</div>
          </div>
          <div v-if="order.cancelled && order.cancelledat" class="row notranslate">
            <span class="label toCapitalize">{{locale.reservation_cancel_date}}</span>
            <div class="value">{{setDateTimeFormat(order.cancelledat)}}</div>
          </div>
          <div class="statusMessage mt15 notranslate">
            <p v-if="orderStatus === 'ready'" class="ready">
              <span v-if="entranceTime && entranceTime.isTimeout">{{ locale.message_reservation_entrance_ready_timeout }}</span>
              <span v-else>{{locale.message_reservation_entrance_ready}}: {{entranceTime.fromStart}} ~ {{entranceTime.fromEnd}}</span>
            </p>
            <p v-else-if="orderStatus === 'progress'" class="progress">{{locale.message_reservation_entrance_progress}}</p>
            <p v-else-if="orderStatus === 'complete'" class="complete">{{locale.message_reservation_entrance_complete}}</p>
            <p v-else-if="orderStatus === 'cancel'" class="cancel">{{locale.reservation_already_cancel}}</p>
          </div>
        </div>
      </div>
      <Qrcode v-if="reservationId && orderId && orderStatus !== 'cancel'"
        :locale="locale"
        :lang="lang"
        :ln="ln"
        :reservationId="reservationId"
        :orderId="orderId" />
      <div v-if="cancellable && cancellable.isCancel && cancellable.ready && !(order.cancelled && order.cancelledat)" class="cancelReservation notranslate">
        <p class="cancelMessage mb10">{{cancellableMessage}}</p>
        <button
          type="submit"
          class="btn btnFull btnWhite"
          :disabled="!cancellable.isCancellable"
          @click="cancel">{{locale.reservation_cancel}}</button>
      </div>
    </div>
    <div v-else-if="order === 'fail'" class="message notranslate">
      <p>{{locale.message_reservation_load_fail}}<br>{{locale.message_reservation_check_id}}</p>
      <span class="reservationId">reservationId: {{ reservationId }}</span>
    </div>
    <div v-else class="message notranslate">{{ locale.data_loading }}</div>
  </div>
  
</template>

<script>
import moment from 'moment/moment'
import { DateTime } from 'luxon'
import Qrcode from '@/components/Reservation/Qrcode'

export default {
  name: 'CheckReservation',
  props: [
    'ln',
    'lang',
    'locale',
    'isPublic',
    'reservationId'
  ],
  components: {
    Qrcode
  },
  computed: {
    timezone () {
      return this.$store.state.siteInfo.timezoneId || ''
    },
    dateFormat () {
      return this.$store.getters['common/getDateFormat']
    },
    format () {
      const setting = this.$store.state.common.setting
      const date = setting.dateFormat[setting.dateformatDefault]
      const time = 'HH:mm:ss'
      return `${date} ${time}`
    },
    summary () {
      return this.order.ordersheet.content
    },
    status () {
      let status = this.locale.reservation_status_ready
      if (this.order.order_status === 'cancel') {
        status = this.locale.reservation_status_cancel
      }
      return status
    },
    seats () {
      return this.autoAssign ? this.summary.seats[0] : this.summary.seats
    },
    orderId () {
      return this.order && this.order.order_uuid
    },
    entranceTime () {
      const date = this.summary && this.summary.date
      const time = this.summary && this.summary.time && this.summary.time.id
      const entrance = this.entrance
      const from = time.slice(0, 4)
      const to = time.slice(-4)
      const fromBefore = entrance ? entrance.fromBefore : 0 // 입장 가능 시간 - 시작시간 N분 전
      const fromAfter = entrance ? entrance.fromAfter : 0 // 입장 마감 시간 - 시작시간 N분 후
      const fromEnd = entrance && entrance.useBlockEnter ? getEndTime(from, fromAfter) : getEndTime(to, 0)
      const isTimeout = moment().isAfter(moment(date + ' ' + getEndTime(entrance && entrance.useBlockEnter ? from : to, fromAfter))) // 입장가능상태인데 입장시간종료?
      function getEndTime (timeId, diff) {
        const hh = timeId.slice(0, 2)
        const mm = timeId.slice(-2)
        return moment().set({
          hour: hh,
          minute: mm
        }).add(diff, 'minutes').format('HH:mm')
      }
      return {
        date: date,
        fromStart: getEndTime(from, -fromBefore),
        fromEnd: fromEnd,
        isTimeout: isTimeout
      }
    },
    cancellableMessage () {
      const cancellable = this.cancellable
      const limitDay = cancellable && cancellable.limitDay
      const limitTime = cancellable && cancellable.limitTime
      return limitDay && limitTime ? this.locale.message_reservation_cancel_time.replace('{var}', `${limitDay} ${limitTime}`) : ''
    }
  },
  created () {
    this.init()
  },
  data () {
    return {
      orderStatus: 'ready',
      order: null,
      cancellable: null,
      info: null,
      autoAssign: true,
      showPlaceName: false,
      showSeatName: false,
      entrance: null
    }
  },
  methods: {
    init () {
      const reservationId = this.reservationId
      function getTimeDisplay (hhmm) {
        const time = hhmm.toString()
        return `${time.substr(0, 2)}:${time.substr(2, 2)}`
      }
      this.$store.dispatch('reservation/getReservationInfo', reservationId).then((res) => {
        if (res) {
          this.order = res
          const property = res.reservationSpaceId
          this.$store.dispatch('reservation/getReservationPropertyInfo', property).then((propertyInfo) => {
            const info = propertyInfo && propertyInfo.payload
            const settings = propertyInfo && propertyInfo.settings
            if (info) {
              // 예약취소 가능여부 확인
              const ready = this.order.order_status === 'ready'
              const date = this.order.ordersheet.content.date
              const time = this.order.ordersheet.content.time.id.substr(0, 4)
              const time2 = this.order.ordersheet.content.time.id.substr(4, 4)
              const isCancel = info.cancel
              const cancelDay = info.cancelDay
              const cancelTime = info.cancelTime
              const endTime = moment().set({ hour: time.substr(0, 2), minute: time.substr(2, 2) }).add(-cancelTime, 'minutes')
              const beforeToday = moment().isBefore(moment(date).add(-cancelDay, 'days'), 'day')
              const isToday = moment().format('YYYY-MM-DD') === date
              const isTime = moment().isBefore(endTime)
              const limitDay = cancelDay > 0 ? this.setDateFormat(moment(date).add(-cancelDay, 'days').format('YYYY-MM-DD')) : this.setDateFormat(date)
              const limitTime = cancelDay > 0 ? '' : getTimeDisplay(endTime.format('HHmm'))
              const selectedSection = info.section.find(x => x.id === this.summary.seats[0].section.id)
              const isEnd = moment().isAfter(moment(date).set({ hour: time2.substr(0, 2), minute: time2.substr(2, 2) })) // 이용시간 종료됨?
              this.cancellable = {
                ready: ready,
                isCancel: isCancel,
                cancelDay: cancelDay,
                beforeToday: beforeToday,
                isToday: isToday,
                isTime: isTime,
                isCancellable: ready && (cancelDay <= 0 ? (beforeToday || isToday && isTime) : moment().isSameOrBefore(moment(date).add(-cancelDay, 'days'), 'day')),
                limitDay: limitDay,
                limitTime: limitTime,
                message: ''
              }
              // 섹션은 모두 동일하므로 첫번째 섹션의 배정정보로 설정
              this.autoAssign = selectedSection && selectedSection.autoAssign
              this.showPlaceName = selectedSection && selectedSection.showPlaceName
              this.showSeatName = selectedSection && selectedSection.showSeatName
              this.orderStatus = this.order.order_status
              this.entrance = this.order.order_status === 'ready' && settings && {
                fromBefore: settings.availableEnterTime,
                fromAfter:  settings.impossibleEnterTime,
                useBlockEnter: settings.useBlockEnter
              }
              if (ready && isEnd) { // ready 상태인데 이용시간이 지남?
                this.orderStatus = 'complete'
              }
            }
          })
        } else {
          this.order = 'fail'
        }
      })
    },
    setDateFormat (date) {
      return moment(date).format(this.dateFormat.toUpperCase())
    },
    setDateTimeFormat (iso) {
      const submitAt = DateTime.fromISO(iso || moment().toISOString(), { zone: this.timezone })
      return submitAt.toFormat(this.format)
    },
    cancel () {
      if (window.confirm(this.locale.confirm_reservation_cancel)) {
        const reservationId = this.reservationId
        this.$store.dispatch('reservation/cancelReservation', reservationId).then((res) => {
          if (res) {
            if (res.includes('ok')) {
              // 취소가 완료되었습니다.
              alert(this.locale.alert_complete_reservation_cancel)
              this.init()
            } else if (res.includes('notFound')) {
              // 예약번호를 찾을 수 없습니다.
              alert(this.locale.alert_notfound_reservation_id)
            } else if (res.inlucdes('alreadyCancel')) {
              // 이미 취소된 예약번호입니다.
              alert(this.locale.alert_already_reservation_cancel)
            }
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content .cartItem {
  margin: 15px;
  .inner {
    display: block;
    padding-bottom: 20px;
  }
  .row {
    display: flex;
    & + .row {
      margin-top: 15px;
    }
  }
  .label {
    color: $color-grey;
  }
  .value {
    margin-left: 1em;
    font-weight: bold;
  }
}
.usableState {
  text-align: center;
  font-size: 1.2em;
  color: $color-lgrey;
  &[data-state="now"] {
    color: $color-green;
  }
}
.cancelReservation {
  position: relative;
  margin-top: 15px;
  padding: 30px 15px;
  text-align: center;
  border-top: 1px dashed $color-lgrey2;
  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 0;
    margin-top: -10px;
    border-style: solid;
    border-width: 10px;
    border-color: transparent;
  }
  &:before {
    left: 0;
    border-left-color: $color-lgrey2;
  }
  &:after {
    right: 0;
    border-right-color: $color-lgrey2;
  }
  .btn {
    border-radius: 4px;
    &[disabled] {
      color: $color-white;
    }
  }
  .cancelMessage {
    font-size: 14px;
    color: $color-lgrey;
  }
}
.message {
  padding-top: 80px;
  padding-bottom: 200px;
  text-align: center;
  font-size: 1.2em;
  p {
    padding-left: 30px;
    padding-right: 30px;
    &:before {
      content: "";
      display: block;
      height: 96px;
      margin-bottom: 30px;
      background: url('~@/assets/img/warn.png') 50% 50% / contain no-repeat;
    }
  }
  .reservationId {
    margin-top: 15px;
    font-size: 14px;
    color: $color-lgrey;
  }
}
.statusMessage {
  text-align: center;
  p {
    display: inline-block;
    padding: 8px 20px;
    color: $color-white;
    border-radius: 16px;
    background-color: $color-dark;
  }
  .progress {
    background-color: $color-green;
  }
  .complete {
    background-color: $color-lgrey;
  }
  .cancel {
    background-color: $color-red;
  }
}
</style>
